import { useQuery } from '@tanstack/react-query';
import type { Asset, Employee, Model } from '@khadi/shared';
import { api, type ListResponse } from './api';

/** Gemeinsame Abfragen — Stammdaten ändern sich selten und werden gecacht. */

export interface NamedEntity {
  id: number;
  name: string;
  is_active: boolean;
  assets_count?: number;
}

export interface StatusEntry {
  id: number;
  name: string;
  is_archived: boolean;
  is_system: boolean;
  sort_order: number;
  assets_count?: number;
}

const LONG = { staleTime: 5 * 60_000 };

export function useStatuses() {
  return useQuery({
    queryKey: ['statuses'],
    queryFn: () => api<ListResponse<StatusEntry>>('/statuses'),
    ...LONG,
  });
}

export function useLocations() {
  return useQuery({
    queryKey: ['locations'],
    queryFn: () => api<ListResponse<NamedEntity>>('/locations'),
    ...LONG,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => api<ListResponse<NamedEntity>>('/categories'),
    ...LONG,
  });
}

export function useManufacturers() {
  return useQuery({
    queryKey: ['manufacturers'],
    queryFn: () => api<ListResponse<NamedEntity>>('/manufacturers'),
    ...LONG,
  });
}

export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api<ListResponse<NamedEntity>>('/suppliers'),
    ...LONG,
  });
}

export function useModels(query: { q?: string; only_used?: boolean } = {}) {
  return useQuery({
    queryKey: ['models', query],
    queryFn: () => api<ListResponse<Model>>('/models', { query: { limit: 500, ...query } }),
    ...LONG,
  });
}

export function useEmployees(query: { q?: string; is_active?: boolean } = {}) {
  return useQuery({
    queryKey: ['employees', query],
    queryFn: () =>
      api<ListResponse<Employee>>('/employees', { query: { limit: 500, sort: 'name', ...query } }),
    staleTime: 60_000,
  });
}

export function useAsset(id: number) {
  return useQuery({
    queryKey: ['asset', id],
    queryFn: () => api<Asset>(`/assets/${id}`),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export interface MovementEntry {
  id: number;
  asset_id: number;
  asset?: { id: number; asset_tag: string; serial: string; model_name: string | null };
  kind: 'create' | 'checkout' | 'checkin' | 'transfer' | 'status' | 'update';
  from: { type: string; id: number | null; name: string } | null;
  to: { type: string; id: number | null; name: string } | null;
  status_from: string | null;
  status_to: string | null;
  note: string | null;
  occurred_at: string;
  actor: string | null;
  source: string;
  has_protocol: boolean;
  confirmed_at: string | null;
  confirmation_pending: boolean;
}

/**
 * Alle Vorgänge einer Person — erhaltene wie abgegebene Geräte.
 *
 * Der Filter `employee_id` greift auf beiden Seiten der Bewegung, damit auch
 * sichtbar bleibt, wohin ein Gerät nach der Rückgabe gegangen ist.
 */
export function useEmployeeMovements(id: number) {
  return useQuery({
    queryKey: ['employee-movements', id],
    queryFn: () =>
      api<ListResponse<MovementEntry>>('/movements', {
        query: { employee_id: id, limit: 100 },
      }),
    enabled: Number.isFinite(id) && id > 0,
  });
}

export function useAssetMovements(id: number) {
  return useQuery({
    queryKey: ['asset-movements', id],
    queryFn: () => api<ListResponse<MovementEntry>>(`/assets/${id}/movements`),
    enabled: Number.isFinite(id) && id > 0,
  });
}
